import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Globe, User } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const links = [
  ["/", "nav_home"],
  ["/about", "nav_about"],
  ["/markets", "nav_markets"],
  ["/vendors", "nav_vendors"],
  ["/artisans", "nav_artisans"],
  ["/workshops", "nav_workshops"],
  ["/apply", "nav_apply"],
];

export function Navbar() {
  const { t, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const dashLink = user && user.role === "admin"
    ? ["/admin", "nav_admin"]
    : user && user.role === "vendor"
      ? ["/vendor", "nav_dashboard"]
      : user ? ["/member", "nav_member"] : null;

  return (
    <header className="sticky top-0 z-50 bg-[#FDFBF7]/85 backdrop-blur-md border-b-2 border-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <span className="font-display text-2xl leading-none">放空</span>
          <span className="hidden sm:inline font-mono-fk text-xs bg-[#FFD93D] fk-border px-2 py-1 rounded">
            The Breathe
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map(([to, key]) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`nav-${key}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-black text-white" : "hover:bg-black/5"
                }`
              }
            >
              {t(key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="flex items-center gap-1 fk-border rounded-full px-3 py-1.5 text-sm font-mono-fk bg-white fk-lift"
          >
            <Globe size={15} />
            {lang === "zh" ? "EN" : "中"}
          </button>

          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              {dashLink && (
                <Link
                  to={dashLink[0]}
                  data-testid="nav-dashboard-link"
                  className="fk-border rounded-full px-3 py-1.5 text-sm font-semibold bg-[#00E5FF] fk-lift flex items-center gap-1"
                >
                  <User size={15} /> {t(dashLink[1])}
                </Link>
              )}
              <button
                onClick={logout}
                data-testid="nav-logout"
                className="text-sm px-3 py-1.5 rounded-full hover:bg-black/5"
              >
                {t("logout")}
              </button>
            </div>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <Link to="/login" data-testid="nav-login" className="text-sm px-3 py-1.5 rounded-full hover:bg-black/5">
                {t("login")}
              </Link>
              <Link
                to="/vendor-register"
                data-testid="nav-join"
                className="fk-border rounded-full px-4 py-1.5 text-sm font-semibold bg-[#FF5757] text-white fk-lift"
              >
                {t("join_market")}
              </Link>
            </div>
          )}

          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t-2 border-black bg-[#FDFBF7] px-4 py-3 space-y-1">
          {links.map(([to, key]) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2 rounded-lg font-medium ${isActive ? "bg-black text-white" : "hover:bg-black/5"}`
              }
            >
              {t(key)}
            </NavLink>
          ))}
          <div className="pt-2 flex flex-wrap gap-2">
            {user ? (
              <>
                {dashLink && (
                  <button onClick={() => { setOpen(false); navigate(dashLink[0]); }} className="fk-border rounded-full px-4 py-2 text-sm bg-[#00E5FF]">
                    {t(dashLink[1])}
                  </button>
                )}
                <button onClick={logout} className="fk-border rounded-full px-4 py-2 text-sm">{t("logout")}</button>
              </>
            ) : (
              <>
                <button onClick={() => { setOpen(false); navigate("/login"); }} className="fk-border rounded-full px-4 py-2 text-sm">{t("login")}</button>
                <button onClick={() => { setOpen(false); navigate("/vendor-register"); }} className="fk-border rounded-full px-4 py-2 text-sm bg-[#FF5757] text-white">{t("join_market")}</button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
